import { CutDirection, KERF } from "./cutUtils";

const EPS = 1e-6;

export const DEFAULT_GENERATE_CONFIG = {
  attempts: 25,
  randomWeight: 12,
  crossSpanDifferenceTolerance: EPS
};

function createSector({
  tableIndex,
  x,
  y,
  w,
  h,
  dir,
  level,
  rootMainDir,
  isFullHeight = false,
  preferredStartRect = null
}) {
  return {
    tableIndex,
    x,
    y,
    w,
    h,
    dir,
    level,
    rootMainDir,
    isFullHeight,
    preferredStartRect
  };
}

function hasValidKerfRemainder(sectorSpan, partSpan) {
  const remainder = sectorSpan - partSpan;
  return remainder >= -EPS &&
    (remainder <= EPS || remainder >= KERF - EPS);
}

function getMainSpan(sector, width, height) {
  return sector.dir === CutDirection.HORIZONTAL ? width : height;
}

function getCrossSpan(sector, width, height) {
  return sector.dir === CutDirection.HORIZONTAL ? height : width;
}

function getNetPreferredSize(preferredStartRect) {
  if (!preferredStartRect) return null;

  return {
    width: Math.max(0, preferredStartRect.width - KERF),
    height: Math.max(0, preferredStartRect.height - KERF)
  };
}

function getOrientations(rect) {
  const orientations = [
    { width: rect.width, height: rect.height, rotated: false }
  ];

  // Forgatott jelölt csak akkor létezhet, ha a darab forgatható.
  if (rect.rotable && Math.abs(rect.width - rect.height) > EPS) {
    orientations.push({
      width: rect.height,
      height: rect.width,
      rotated: true
    });
  }

  return orientations;
}

function pickRandomizedBestThatFits(
  sector,
  remainingRects,
  prevRect,
  isSectorStart,
  config
) {
  const sectorArea = Math.max(EPS, sector.w * sector.h);
  const sectorMainSpan = Math.max(EPS, getMainSpan(sector, sector.w, sector.h));
  const sectorCrossSpan = Math.max(
    EPS,
    getCrossSpan(sector, sector.w, sector.h)
  );
  const preferred = getNetPreferredSize(sector.preferredStartRect);
  let best = null;

  for (let index = 0; index < remainingRects.length; index++) {
    const rect = remainingRects[index];

    for (const orientation of getOrientations(rect)) {
      const { width, height, rotated } = orientation;
      if (width <= EPS || height <= EPS) continue;
      if (width > sector.w + EPS || height > sector.h + EPS) continue;
      const pairingHorizontal = config.pairAvailable != null && sector.dir === CutDirection.HORIZONTAL;
      const pairingVertical = config.pairAvailable != null && sector.dir === CutDirection.VERTICAL;
      if (!pairingHorizontal && !hasValidKerfRemainder(sector.w, width)) continue;
      if (!pairingVertical && !hasValidKerfRemainder(sector.h, height)) continue;
      if (config.pairAvailable != null && !hasValidKerfRemainder(
        config.pairAvailable, 2 * (pairingHorizontal ? width : height) + KERF
      )) continue;

      const mainSpan = getMainSpan(sector, width, height);
      const crossSpan = getCrossSpan(sector, width, height);
      const areaFill = (width * height) / sectorArea;
      const mainFill = mainSpan / sectorMainSpan;
      const crossFill = crossSpan / sectorCrossSpan;
      const orderBias = 1 - index / Math.max(1, remainingRects.length);

      const sameAsPrevious =
        prevRect != null &&
        Math.abs(width - prevRect.width) < EPS &&
        Math.abs(height - prevRect.height) < EPS;
      const previousCrossSpan = prevRect
        ? getCrossSpan(sector, prevRect.width, prevRect.height)
        : null;

      let preferredMatch = 0;
      let preferredCrossSpan = null;
      if (isSectorStart && preferred) {
        preferredCrossSpan = getCrossSpan(
          sector,
          preferred.width,
          preferred.height
        );
        preferredMatch = Math.max(
          0,
          1 - Math.abs(crossSpan - preferredCrossSpan) / sectorCrossSpan
        );
      }

      const referenceCrossSpan = previousCrossSpan ?? preferredCrossSpan;
      const crossSpanDifference =
        referenceCrossSpan == null
          ? null
          : Math.abs(crossSpan - referenceCrossSpan);
      const score =
        (sameAsPrevious ? 70 : 0) +
        preferredMatch * 35 +
        crossFill * 32 +
        mainFill * 18 +
        areaFill * 28 +
        orderBias * 10 +
        Math.random() * config.randomWeight;

      // A legkisebb szélességkülönbség elsődleges. A pontszám és a random
      // csak azonos (a tolerancián belüli) különbség esetén dönthet.
      const hasCloserCrossSpan =
        best != null &&
        crossSpanDifference != null &&
        crossSpanDifference <
          best.crossSpanDifference - config.crossSpanDifferenceTolerance;
      const hasEquivalentCrossSpanDifference =
        best != null &&
        (crossSpanDifference == null ||
          Math.abs(crossSpanDifference - best.crossSpanDifference) <=
            config.crossSpanDifferenceTolerance);

      const packingPriority = config.placementStrategy === "area"
        ? width * height
        : config.placementStrategy === "wide" ? crossSpan : null;
      const matchesCrossSpan = crossSpanDifference != null && crossSpanDifference < EPS;
      const betterGrouped = !best ||
        hasCloserCrossSpan ||
        (hasEquivalentCrossSpanDifference && (
          (sameAsPrevious && !best.sameAsPrevious) ||
          (sameAsPrevious === best.sameAsPrevious && score > best.score)
        ));
      // Keep matching strip sizes together in every strategy. Area/width
      // alternatives still decide when no exact cross-span match fits.
      if (!best || (matchesCrossSpan && !best.matchesCrossSpan) ||
        (matchesCrossSpan === best.matchesCrossSpan && (packingPriority == null ? betterGrouped :
        packingPriority > best.packingPriority + EPS ||
        (Math.abs(packingPriority - best.packingPriority) < EPS && betterGrouped)))) {
        best = {
          matchesCrossSpan,
          packingPriority,
          score,
          sameAsPrevious,
          crossSpanDifference,
          index,
          rect: {
            ...rect,
            width,
            height,
            rotated
          }
        };
      }
    }
  }

  return best;
}

// Reserve a counterpart before filling the first sector, so its inner cuts
// cannot consume the pieces needed to build the identical neighbouring sector.
function fillSectorPair(sector, remainingRects, placedRects, tableIndex, id, config) {
  const horizontal = sector.dir === CutDirection.HORIZONTAL;
  const available = horizontal ? sector.w : sector.h;
  const pool = [];
  const pairs = new Map();
  const reserved = new Set();
  for (const first of remainingRects) {
    if (reserved.has(first)) continue;
    let sharedOrientation;
    const second = remainingRects.find((candidate) => candidate !== first &&
      !reserved.has(candidate) && getOrientations(first).some((firstOrientation) => {
        const matches = getOrientations(candidate).some((o) =>
          Math.abs(o.width - firstOrientation.width) < EPS &&
          Math.abs(o.height - firstOrientation.height) < EPS);
        if (matches) sharedOrientation = firstOrientation;
        return matches;
      }));
    if (!second) continue;
    reserved.add(first);
    reserved.add(second);
    const representative = {
      ...first, width: sharedOrientation.width, height: sharedOrientation.height,
      rotable: first.rotable && second.rotable
    };
    pool.push(representative);
    pairs.set(first.localid, [first, second]);
  }
  if (!pool.length) return null;
  const halfSector = {
    ...sector,
    ...(horizontal ? { w: (available - KERF) / 2 } : { h: (available - KERF) / 2 })
  };
  // The half-sector is only a selection bound, not a physical cut boundary.
  const pick = pickRandomizedBestThatFits(halfSector, pool, null, true, {
    ...config, pairAvailable: available
  });
  if (!pick) return null;
  const seed = pick.rect;
  const span = horizontal ? seed.width : seed.height;
  if (!hasValidKerfRemainder(available, 2 * span + KERF)) return null;
  const firstSector = [{
    ...seed, xpos: sector.x, ypos: sector.y,
    width: seed.width + KERF, height: seed.height + KERF,
    tableIndex, id, rootMainDir: sector.rootMainDir, isLastInSector: false
  }];
  const innerPool = pool.filter((r) => r.localid !== seed.localid);
  fillSector(createSector({
    tableIndex,
    x: horizontal ? sector.x : sector.x + seed.width + KERF,
    y: horizontal ? sector.y + seed.height + KERF : sector.y,
    w: horizontal ? seed.width : sector.w - seed.width - KERF,
    h: horizontal ? sector.h - seed.height - KERF : seed.height,
    dir: horizontal ? CutDirection.VERTICAL : CutDirection.HORIZONTAL,
    level: 1, rootMainDir: sector.rootMainDir,
    preferredStartRect: firstSector[0]
  }), innerPool, firstSector, tableIndex, id, config);
  firstSector[firstSector.length - 1].isLastInSector = true;
  const consumed = new Set();
  const copies = [[], []];
  for (const placed of firstSector) {
    pairs.get(placed.localid).forEach((source, index) => {
      consumed.add(source);
      const orientation = getOrientations(source).find((o) =>
        Math.abs(o.width - (placed.width - KERF)) < EPS &&
        Math.abs(o.height - (placed.height - KERF)) < EPS);
      copies[index].push({
        ...source, width: placed.width, height: placed.height,
        rotated: orientation.rotated,
        xpos: placed.xpos + (horizontal ? index * (span + KERF) : 0),
        ypos: placed.ypos + (horizontal ? 0 : index * (span + KERF)),
        tableIndex, id, rootMainDir: sector.rootMainDir,
        isLastInSector: placed.isLastInSector
      });
    });
  }
  placedRects.push(...copies[0], ...copies[1]);
  for (let index = remainingRects.length - 1; index >= 0; index--) {
    if (consumed.has(remainingRects[index])) remainingRects.splice(index, 1);
  }
  return { advance: 2 * (span + KERF), seed };
}

// A processSectorFillOnTheFly szektorbejárása vágáskezelés nélkül.
function fillSector(
  sector,
  remainingRects,
  placedRects,
  tableIndex,
  id,
  config
) {
  if (!sector || remainingRects.length === 0) return;

  if (sector.dir === CutDirection.HORIZONTAL) {
    let x = sector.x;
    let availableWidth = sector.w;
    const startIndex = placedRects.length;
    let previousRect = null;

    while (availableWidth > EPS && remainingRects.length > 0) {
      const activeSector = { ...sector, x, w: availableWidth };
      if (config.pairSectors && sector.level === 0) {
        const pair = fillSectorPair(activeSector, remainingRects, placedRects, tableIndex, id, config);
        if (pair) {
          x += pair.advance;
          availableWidth -= pair.advance;
          previousRect = pair.seed;
          continue;
        }
      }
      const pick = pickRandomizedBestThatFits(
        activeSector,
        remainingRects,
        previousRect,
        previousRect == null,
        config
      );

      if (!pick) break;

      const { rect, index } = pick;
      const width = rect.width;
      const height = rect.height;
      const placedRect = {
        ...rect,
        xpos: x,
        ypos: sector.y,
        width: width + KERF,
        height: height + KERF,
        tableIndex,
        id,
        rootMainDir: sector.rootMainDir,
        isLastInSector: false
      };

      placedRects.push(placedRect);
      remainingRects.splice(index, 1);
      previousRect = rect;

      const belowHeight = sector.h - (height + KERF);
      if (belowHeight > EPS) {
        fillSector(
          createSector({
            tableIndex,
            x,
            y: sector.y + height + KERF,
            w: width,
            h: belowHeight,
            dir: CutDirection.VERTICAL,
            level: sector.level + 1,
            rootMainDir: sector.rootMainDir,
            preferredStartRect: placedRect
          }),
          remainingRects,
          placedRects,
          tableIndex,
          id,
          config
        );
      }

      x += width + KERF;
      availableWidth -= width + KERF;
    }

    if (placedRects.length > startIndex) {
      placedRects[placedRects.length - 1].isLastInSector = true;
    }
    return;
  }

  let y = sector.y;
  let availableHeight = sector.h;
  const startIndex = placedRects.length;
  let previousRect = null;

  while (availableHeight > EPS && remainingRects.length > 0) {
    const activeSector = { ...sector, y, h: availableHeight };
    if (config.pairSectors && sector.level === 0) {
      const pair = fillSectorPair(activeSector, remainingRects, placedRects, tableIndex, id, config);
      if (pair) {
        y += pair.advance;
        availableHeight -= pair.advance;
        previousRect = pair.seed;
        continue;
      }
    }
    const pick = pickRandomizedBestThatFits(
      activeSector,
      remainingRects,
      previousRect,
      previousRect == null,
      config
    );

    if (!pick) break;

    const { rect, index } = pick;
    const width = rect.width;
    const height = rect.height;
    const placedRect = {
      ...rect,
      xpos: sector.x,
      ypos: y,
      width: width + KERF,
      height: height + KERF,
      tableIndex,
      id,
      rootMainDir: sector.rootMainDir,
      isLastInSector: false
    };

    placedRects.push(placedRect);
    remainingRects.splice(index, 1);
    previousRect = rect;

    const rightWidth = sector.w - (width + KERF);
    if (rightWidth > EPS) {
      fillSector(
        createSector({
          tableIndex,
          x: sector.x + width + KERF,
          y,
          w: rightWidth,
          h: height,
          dir: CutDirection.HORIZONTAL,
          level: sector.level + 1,
          rootMainDir: sector.rootMainDir,
          isFullHeight: sector.isFullHeight,
          preferredStartRect: placedRect
        }),
        remainingRects,
        placedRects,
        tableIndex,
        id,
        config
      );
    }

    y += height + KERF;
    availableHeight -= height + KERF;
  }

  if (placedRects.length > startIndex) {
    placedRects[placedRects.length - 1].isLastInSector = true;
  }
}

function shuffledByHeuristic(rects, rootDir, attempt) {
  const result = rects.map((rect) => ({ ...rect }));

  if (attempt === 0) {
    return result.sort((a, b) => b.width * b.height - a.width * a.height);
  }
  if (attempt === 1) {
    return result.sort((a, b) =>
      rootDir === CutDirection.HORIZONTAL
        ? b.height - a.height
        : b.width - a.width
    );
  }
  if (attempt === 2) {
    return result.sort(
      (a, b) =>
        Math.max(b.width, b.height) - Math.max(a.width, a.height)
    );
  }

  const maxArea = Math.max(
    EPS,
    ...result.map((rect) => rect.width * rect.height)
  );

  return result
    .map((rect) => {
      const areaWeight = 1 + (rect.width * rect.height) / maxArea;
      const randomKey = -Math.log(Math.max(EPS, Math.random())) / areaWeight;
      return { rect, randomKey };
    })
    .sort((a, b) => a.randomKey - b.randomKey)
    .map(({ rect }) => rect);
}

function scoreLayout(placedRects, totalRects, sheetW, sheetH) {
  const usedArea = placedRects.reduce(
    (sum, rect) => sum + (rect.width - KERF) * (rect.height - KERF),
    0
  );
  const sheetArea = Math.max(EPS, sheetW * sheetH);
  const placedRatio = totalRects > 0 ? placedRects.length / totalRects : 0;
  const usageRatio = usedArea / sheetArea;

  return {
    score: placedRatio * 0.6 + usageRatio * 0.4,
    usedArea,
    placedRatio,
    wasteRatio: Math.max(0, 1 - usageRatio)
  };
}

export function generateLayouts({
  rootDir,
  rects,
  sheetW,
  sheetH,
  tableIndex,
  id,
  TRIM,
  config = {}
}) {
  const cfg = { ...DEFAULT_GENERATE_CONFIG, ...config };
  const attemptCount = Math.max(1, Math.floor(cfg.attempts));
  const layouts = [];

  for (let attempt = 0; attempt < attemptCount; attempt++) {
    const remainingRects = shuffledByHeuristic(rects, rootDir, attempt);
    const placedRects = [];

    fillSector(
      createSector({
        tableIndex,
        x: TRIM,
        y: TRIM,
        w: sheetW - TRIM,
        h: sheetH - TRIM,
        dir: rootDir,
        level: 0,
        rootMainDir: rootDir,
        isFullHeight: true
      }),
      remainingRects,
      placedRects,
      tableIndex,
      id,
      {
        ...cfg,
        pairSectors: cfg.pairSectors ?? (
          (!cfg.placementStrategy || cfg.placementStrategy === "grouped") && attempt % 3 !== 0
        )
      }
    );

    const stats = scoreLayout(
      placedRects,
      rects.length,
      sheetW,
      sheetH
    );

    layouts.push({
      placedRects,
      remainingRects,
      rootMainDir: rootDir,
      _layoutStats: stats
    });
  }

  return layouts;
}

/**
 * Önálló használatnál visszaadja a terület és darabszám alapján legjobb
 * layoutot. A teljes táblagenerálás a generateLayouts mind a 25 eredményét
 * a hozzájuk tartozó vágáshosszal együtt pontozza.
 */
export function generate(args) {
  const layouts = generateLayouts(args);
  const best = layouts.reduce(
    (currentBest, layout) =>
      !currentBest ||
      layout._layoutStats.score > currentBest._layoutStats.score + EPS ||
      (Math.abs(
        layout._layoutStats.score - currentBest._layoutStats.score
      ) < EPS &&
        layout._layoutStats.usedArea > currentBest._layoutStats.usedArea + EPS)
        ? layout
        : currentBest,
    null
  );

  return {
    placedRects: best?.placedRects ?? [],
    remainingRects: best?.remainingRects ?? [...(args.rects ?? [])],
    rootMainDir: args.rootDir,
    _beamStats: {
      bestScore: best?._layoutStats.score ?? 0,
      bestWasteRatio: best?._layoutStats.wasteRatio ?? 1,
      bestPlacedRatio: best?._layoutStats.placedRatio ?? 0,
      candidatesEvaluated: layouts.length
    }
  };
}
